import { useState, useEffect, useRef } from 'react';
import { buildTeamApiService } from '../services/buildTeamApi';

/**
 * Fetches authenticated project media bytes and exposes a blob URL for <img src>.
 */
export function useBuildProjectMediaUrl(mediaId: string | undefined | null): {
  url: string | null;
  loading: boolean;
  error: string | null;
} {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!mediaId);
  const [error, setError] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    const revoke = () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };

    if (!mediaId) {
      revoke();
      setUrl(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    revoke();
    setUrl(null);
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await buildTeamApiService.downloadProjectMedia(mediaId);
        const blob = await res.blob();
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        objectUrlRef.current = objectUrl;
        setUrl(objectUrl);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load image');
          setUrl(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      revoke();
    };
  }, [mediaId]);

  return { url, loading, error };
}
